/**
 * Solana transaction 由来 action の保存と fiscal lock 退避を担当する。
 */
import type { Action } from '@0xtorch/core'
import { divideArrayIntoChunks, tokenTransferCountVersion } from '@pkg/basic'
import {
  type TransactionDetail,
  createSolanaActionSource,
  createSolanaAddressId,
} from '@pkg/solana'
import { eq, inArray, sql } from 'drizzle-orm'
import {
  actionSourceColumnCount,
  sqliteMaxHostParameterCount,
} from '../../constants'
import {
  accountSolanaSignatureTable,
  accountTable,
  actionSourceTable,
  actionTable,
  lockedPeriodActionTable,
  portfolioTable,
  solanaSignatureTable,
} from '../../schema'
import type { DatabaseWithTransaction } from '../../types'
import { conflictUpdateAllExcept, getMaxInsertRowCount } from '../../utils'
import { applyActionEditRulesToInsertedActionsOnDatabase } from './applyActionEditRulesToInsertedActions'
import { deleteAssistantProposalsForSourceIds } from './deleteAssistantProposalsForActionKeys'
import {
  applyRulesToLockedInsertRowsInTransaction,
  createAssetInsertQueries,
  createLockedActionInsertQueries,
  createOpenActionInsertQueries,
  filterActionAccountRelations,
  getClosedAccountingPeriodBoundary,
  partitionSourceIdsByFiscalLock,
  prepareFiscalLockInsertRows,
} from './fiscalLock'

type SaveSolanaTxAndActionsParameters = {
  database: DatabaseWithTransaction
  transactions: readonly TransactionDetail[]
  actions: readonly Action[]
  tokenTransferCounts?: ReadonlyMap<string, number>
}

/** Solana transaction から生成したデータを保存
 * - portfolio.isTxGenerated を false に更新
 * - transaction に関連する accountSolanaSignature.analyzed を true に更新
 * - transaction を actionSource に保存 (upsert)
 * - actionSource に関連する既存データ (action と cascade 要素) を削除
 * - action 関連データを保存
 *   - crypto currency
 *   - nft
 *   - action
 *   - action transfer
 *   - action account relation
 * - account.syncing を更新
 */
export const saveSolanaTxAndActions = async ({
  database: { database, transaction },
  transactions,
  actions,
  tokenTransferCounts,
}: SaveSolanaTxAndActionsParameters) => {
  if (transactions.length === 0 && actions.length === 0) {
    return
  }

  const signatures: string[] = []
  for (const transaction of transactions) {
    const signature = transaction.transaction.signatures[0]
    signatures.push(signature)
  }
  const closedBoundary = await getClosedAccountingPeriodBoundary({
    database: { database, transaction },
  })
  const signatureTimestampRows =
    signatures.length === 0
      ? []
      : await database
          .select({
            signature: solanaSignatureTable.signature,
            timestamp: solanaSignatureTable.timestamp,
          })
          .from(solanaSignatureTable)
          .where(inArray(solanaSignatureTable.signature, [...signatures]))
  const signatureTimestampMap = new Map(
    signatureTimestampRows.map((row) => [
      row.signature,
      row.timestamp.getTime(),
    ]),
  )
  const { openSourceIds, lockedSourceIds, unclassifiedSourceIds } =
    partitionSourceIdsByFiscalLock({
      sourceRows: signatures.map((signature) => ({
        sourceId: createSolanaActionSource({ signature }),
        timestamp: signatureTimestampMap.get(signature),
      })),
      closedBoundary,
    })
  const deletingOpenSourceIds = [
    ...new Set([...openSourceIds, ...unclassifiedSourceIds]),
  ]
  const deletingLockedSourceIds = [
    ...new Set([...lockedSourceIds, ...unclassifiedSourceIds]),
  ]

  const accountRows = await database
    .select({ id: accountTable.id, address: accountTable.solanaAddress })
    .from(accountTable)
    .where(eq(accountTable.type, 'solana'))
  const accounts: { id: number; address: string }[] = []
  for (const row of accountRows) {
    if (row.address === null) {
      continue
    }
    accounts.push({ id: row.id, address: row.address })
  }
  const actionAccountRelations = generateActionAccountRelations({
    transactions,
    actions,
    accounts,
  })

  const {
    openActionIds: insertedActionIdList,
    openInsertRows,
    lockedInsertRows,
  } = await prepareFiscalLockInsertRows({
    database: { database, transaction },
    actions,
    createLockedActionAccountRelations: ({ lockedActions }) =>
      filterActionAccountRelations({
        actionAccountRelations,
        actionIds: lockedActions.map(
          (action) => `${action.source}_${action.order}`,
        ),
      }),
  })
  const shouldInvalidateCalculation =
    deletingOpenSourceIds.length > 0 || insertedActionIdList.length > 0

  await transaction([
    (tx) => [
      deleteAssistantProposalsForSourceIds({
        database: tx,
        sourceIds: deletingOpenSourceIds,
      }),
    ],
    (tx) => [
      ...(shouldInvalidateCalculation
        ? [tx.update(portfolioTable).set({ isTxGenerated: false })]
        : []),
      // transaction に関連する accountSolanaSignature.analyzed を true に更新
      ...divideArrayIntoChunks(signatures, sqliteMaxHostParameterCount - 2).map(
        (chunk) =>
          tx
            .update(accountSolanaSignatureTable)
            .set({ analyzed: true })
            .where(inArray(accountSolanaSignatureTable.signature, [...chunk])),
      ),
      // transaction を actionSource に保存 (upsert)
      ...divideArrayIntoChunks(
        transactions,
        getMaxInsertRowCount(actionSourceColumnCount, true),
      ).map((chunk) =>
        tx
          .insert(actionSourceTable)
          .values(
            chunk.map((transaction) => ({
              id: createSolanaActionSource({
                signature: transaction.transaction.signatures[0],
              }),
              type: 'solana' as const,
              solanaSignature: transaction.transaction.signatures[0],
              solanaStatus:
                transaction.meta?.err === null
                  ? ('success' as const)
                  : ('reverted' as const),
              tokenTransferCount: tokenTransferCounts?.get(
                transaction.transaction.signatures[0],
              ),
              tokenTransferCountVersion:
                tokenTransferCounts?.has(
                  transaction.transaction.signatures[0],
                ) === true
                  ? tokenTransferCountVersion.solana
                  : undefined,
            })),
          )
          .onConflictDoUpdate({
            target: [actionSourceTable.id],
            set: {
              ...conflictUpdateAllExcept(actionSourceTable, [
                'id',
                'tokenTransferCount',
                'tokenTransferCountVersion',
              ]),
              tokenTransferCount: sql`coalesce(excluded."tokenTransferCount", ${actionSourceTable.tokenTransferCount})`,
              tokenTransferCountVersion: sql`coalesce(excluded."tokenTransferCountVersion", ${actionSourceTable.tokenTransferCountVersion})`,
            },
          }),
      ),
      // actionSource に関連する action 削除
      ...divideArrayIntoChunks(
        deletingOpenSourceIds,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx.delete(actionTable).where(inArray(actionTable.sourceId, [...chunk])),
      ),
      ...divideArrayIntoChunks(
        deletingLockedSourceIds,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .delete(lockedPeriodActionTable)
          .where(inArray(lockedPeriodActionTable.sourceId, [...chunk])),
      ),
    ],
    (tx) => [
      ...createAssetInsertQueries({ tx, openInsertRows, lockedInsertRows }),
    ],
    (tx) => [
      (async () => {
        const preparedLockedInsertRows =
          await applyRulesToLockedInsertRowsInTransaction({
            database: tx,
            lockedInsertRows,
          })

        await Promise.all([
          ...createOpenActionInsertQueries({
            tx,
            openInsertRows,
            actionAccountRelationRows: filterActionAccountRelations({
              actionAccountRelations,
              actionIds: insertedActionIdList,
            }),
          }),
          ...accounts.map(({ id }) =>
            tx
              .update(accountTable)
              .set({
                syncing: sql`(EXISTS (SELECT 1 FROM ${accountSolanaSignatureTable} WHERE ${accountSolanaSignatureTable.accountId} = ${id} AND ${accountSolanaSignatureTable.analyzed} = ${false}))`,
              })
              .where(eq(accountTable.id, id)),
          ),
          ...createLockedActionInsertQueries({
            tx,
            lockedInsertRows: preparedLockedInsertRows,
          }),
        ])

        if (insertedActionIdList.length > 0) {
          await applyActionEditRulesToInsertedActionsOnDatabase({
            database: tx,
            actionIdList: insertedActionIdList,
          })
        }
      })(),
    ],
  ])
}

/** transaction と action から actionAccountRelation data を作成
 * - action transfer の from, to に account address が含まれていれば account に関連づける
 * - transaction signer に account address が含まれていれば account に関連づける
 */
const generateActionAccountRelations = (params: {
  transactions: readonly TransactionDetail[]
  actions: readonly Action[]
  accounts: readonly { id: number; address: string }[]
}): readonly { actionId: string; accountId: number }[] => {
  const { transactions, actions, accounts } = params

  const addressMap = new Map<string, number>(
    accounts.map(({ address, id }) => [address, id]),
  )
  const solanaAddressIdMap = new Map<string, number>(
    accounts.map(({ address, id }) => [createSolanaAddressId({ address }), id]),
  )
  const sourceSignersMap = new Map<string, string[]>(
    transactions.map((tx) => [
      createSolanaActionSource({ signature: tx.transaction.signatures[0] }),
      tx.transaction.message.accountKeys
        .map((key) => (key.signer ? key.pubkey : undefined))
        .filter((signer): signer is string => signer !== undefined),
    ]),
  )

  const relationMap = new Map<string, { actionId: string; accountId: number }>()
  for (const action of actions) {
    const actionId = `${action.source}_${action.order}`
    // signers と一致する account との relation data 作成
    const signers = sourceSignersMap.get(action.source)
    for (const signer of signers ?? []) {
      const signerAccountId = addressMap.get(signer)
      if (signerAccountId !== undefined) {
        const id = `${actionId}/${signerAccountId}`
        if (!relationMap.has(id)) {
          relationMap.set(actionId, { actionId, accountId: signerAccountId })
        }
      }
    }

    // action transfer の from, to と一致する account を追加
    for (const transfer of action.transfers) {
      const transferFromAccountId =
        transfer.from === undefined
          ? undefined
          : solanaAddressIdMap.get(transfer.from)
      if (transferFromAccountId !== undefined) {
        const id = `${actionId}/${transferFromAccountId}`
        if (!relationMap.has(id)) {
          relationMap.set(actionId, {
            actionId,
            accountId: transferFromAccountId,
          })
        }
      }
      const transferToAccountId =
        transfer.to === undefined
          ? undefined
          : solanaAddressIdMap.get(transfer.to)
      if (transferToAccountId !== undefined) {
        const id = `${actionId}/${transferToAccountId}`
        if (!relationMap.has(id)) {
          relationMap.set(actionId, {
            actionId,
            accountId: transferToAccountId,
          })
        }
      }
    }
  }

  return [...relationMap.values()]
}
